import styled, { css, type RuleSet } from 'styled-components'
import { border1, darkOrange, gold, orange, textMuted, warmTint, white } from '@/theme/colors'
import { fontDisplay } from '@/theme/fonts'
import { fontSizes, leading } from '@/theme/typography'

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'
export type AvatarTone = 'accent' | 'gold' | 'soft' | 'muted'

/** Circle diameter and the initials' size inside it. `sm` sits below the ramp on purpose: two letters in a 24px circle. */
export const avatarSizes: Record<AvatarSize, { box: number; font: string }> = {
	sm: { box: 24, font: '9px' },
	md: { box: 32, font: fontSizes.micro },
	lg: { box: 40, font: fontSizes.small },
	xl: { box: 56, font: fontSizes.title },
}

const tones: Record<AvatarTone, RuleSet<object>> = {
	/** The default: orange ground, white initials. */
	accent: css`
		background: ${orange};
		color: ${white};
	`,
	/** The "+N" overflow bubble at the end of a stack. */
	gold: css`
		background: ${gold};
		color: ${white};
	`,
	/** Warm tint ground, dark orange glyph — member cards. */
	soft: css`
		background: ${warmTint};
		color: ${darkOrange};
	`,
	/** Nobody selected. */
	muted: css`
		background: ${border1};
		color: ${textMuted};
	`,
}

export const AvatarRoot = styled.span<{ $size: AvatarSize; $tone: AvatarTone; $ring: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${({ $size }) => avatarSizes[$size].box}px;
	height: ${({ $size }) => avatarSizes[$size].box}px;
	border-radius: 50%;
	overflow: hidden;
	font-family: ${fontDisplay};
	font-weight: 700;
	font-size: ${({ $size }) => avatarSizes[$size].font};
	line-height: ${leading.none};
	user-select: none;
	${({ $tone }) => tones[$tone]}
	${({ $ring }) =>
		$ring &&
		css`
			border: 2px solid ${white};
		`}
`

export const AvatarImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
`
